import { apiFetch } from '../../services/http';
import type { GoogleProposalResult, GoogleProposals } from './googleProposals';
import type { GoogleIntelligenceRecord } from './googleIntelligence';

export interface Campaign {
  id: string;
  name: string;
  status: string;
  message_template?: string;
  target_audience: any;
  scheduled_for?: string;
  // Esteira v2
  campaign_type: 'whatsapp_blast' | 'meta_ads';
  ad_platform?: string;
  objective?: string;
  visual_state?: 'backlog' | 'todo' | 'in-progress' | 'done';
  meta_sync_status?: 'none' | 'pending' | 'synced' | 'error' | 'status_changing';
  primary_asset_url?: string;
  landing_url?: string;
  meta_campaign_id?: string;
  meta_ad_account_id?: string;
  meta_ad_id?: string;
  meta_adset_id?: string;
  meta_creative_id?: string;
  meta_last_error?: string;
  meta_confirmed_status?: string | null;
  meta_status_check_error?: string | null;
  meta_status_checked_at?: string | null;
  
  // Google Ads fields
  google_customer_id?: string;
  google_campaign_id?: string;
  google_ad_group_id?: string;
  google_ad_id?: string;
  google_sync_status?: string;
  google_last_error?: string;
  google_confirmed_status?: string | null;
  google_status_check_error?: string | null;
  google_status_checked_at?: string | null;
  google_published_at?: string | number;
  google_campaign_type?: string;
  google_keywords?: string[] | string;
  google_headlines?: string[] | string;
  google_descriptions?: string[] | string;
  
  // Intake / Briefing data (for creation)
  product_or_offer?: string;
  target_audience_description?: string;
  budget_initial?: number;

  // Enriched Kanban data
  budget?: {
    daily: number;
    total: number | null;
    spent?: number | null;
    currency: string;
  } | null;
  creatives?: {
    id: string;
    type: string;
    headline: string | null;
    body: string | null;
    asset_url: string | null;
    cta_type: string | null;
    status: string | null;
  }[];

  created_at: string;
  updated_at: string;
}

export interface CampaignEvent {
  id: string;
  campaign_id: string;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  details: any;
  actor_id: string;
  created_at: string;
}

export interface TransitionResult {
  ok: boolean;
  from: string;
  to: string;
  visual_state: string;
  jobEnqueued: string | null;
  jobId: string | null;
}

export interface MetaAdsAccount {
  id: string;
  adAccountId: string;
  pageId: string | null;
  name: string;
  pageName: string | null;
  isDefault: boolean;
}

export interface TenantAvatar {
  id: string;
  tenant_id: string;
  gender: 'male' | 'female';
  name: string;
  role_title?: string;
  appearance_prompt: string;
  style_guidelines?: string;
  asset_url?: string;
  is_active: number;
}

export type VideoProvider = 'fal' | 'replicate' | 'omnirouter' | 'kling';

export interface VideoProviderStatus {
  provider: VideoProvider;
  configured: boolean;
  maskedKey: string | null;
  invalidReason?: string | null;
}

export interface VideoKeyStatus {
  configured: boolean;
  provider: VideoProvider | null;
  maskedKey: string | null;
  providers: VideoProviderStatus[];
}

export interface RemoteMetaCampaign {
  id: string;
  name: string;
  status: string;
  effectiveStatus: string;
  objective: string;
  updatedTime: string | null;
  isImported: boolean;
  localCampaignId: string | null;
  localStatus: string | null;
}

export interface RemoteMetaCampaignsResponse {
  adAccountId: string;
  pageId?: string;
  total: number;
  items: RemoteMetaCampaign[];
}

export interface ImportMetaCampaignsResponse {
  success: boolean;
  importedCount: number;
  items: Array<{
    localCampaignId: string;
    metaCampaignId: string;
    name: string;
    status: string;
    creativesCount: number;
    isNew: boolean;
  }>;
}

export interface CampaignBriefing {
  id?: string;
  campaignId?: string;
  productOrOffer: string;
  targetAudienceDescription: string;
  valueProposition?: string;
  painPoints?: string;
  toneOfVoice?: string;
  notes?: string;
  status?: 'draft' | 'approved';
  visualOverlayMode?: 'text' | 'image_in_image' | 'pure_image';
  logoOverlayUrl?: string | null;
  includeImage?: boolean;
  includeText?: boolean;
  includeAvatar?: boolean;
  avatarGenderPreference?: 'male' | 'female' | 'auto';
  selectedAvatarId?: string | null;
  customOverlayText?: string | null;
  budgetDaily?: number;
  budgetTotal?: number;
  destinationType?: 'whatsapp' | 'lead_form' | 'landing_page' | 'checkout';
  landingUrl?: string;
  geoTargeting?: string;
  interests?: string;
  metaAdAccountId?: string | null;
  metaPageId?: string | null;
  googleCustomerId?: string | null;
  adChannels?: ('meta' | 'google')[];
  productId?: string | null;
  selectedKeywords?: string[] | string;
  keywordResearchData?: KeywordResearchResult | string | null;
  expandedPains?: string[] | string;
  approvedAt?: string | null;
  updatedAt?: string;
}

export interface LogoPlacement {
  x: number;       // % from left
  y: number;       // % from top
  scale: number;   // % of image width
  rotation: number; // degrees
  opacity: number;  // 0-1
  anchor: string;
  blendHint: string;
}

export interface CampaignAudience {
  id: string;
  name: string;
  description: string;
  rules?: string;
  interests?: string;
  estimatedSize?: number;
}

export interface CampaignCreative {
  id: string;
  name: string;
  stepOrder?: number;
  consciousnessLevel?: 'unaware' | 'problem_aware' | 'solution_aware' | 'product_aware' | 'most_aware';
  angleName?: string;
  headline: string;
  primaryText: string;
  callToActionType: string;
  visualMetaphor?: string;
  imagePrompt?: string;
  overlayText?: string;
  assetUrl?: string;
  videoUrl?: string;
  videoPrompt?: string;
  videoModel?: string;
  videoStatus?: 'idle' | 'generating' | 'completed' | 'failed';
  videoError?: string;
  videoDurationSeconds?: number;
  videoScript?: string;
  videoScenes?: Array<{ act: number; type: string; duration_s: number; visual_description: string; narration_text: string }> | string;
  videoNarrationUrl?: string;
  imageHistory?: Array<{ id: string; url: string; isActive?: boolean; createdAt?: string }>;
  status?: string;
}

export interface KeywordSuggestion {
  keyword: string;
  score: number;
  trend: 'rising' | 'stable' | 'declining';
  intent: 'awareness' | 'consideration' | 'decision';
  consciousnessLevel: 'unaware' | 'problem_aware' | 'solution_aware' | 'product_aware' | 'most_aware';
  source: 'google_autocomplete' | 'google_trends' | 'gemini_ai' | 'product_catalog';
  approved?: boolean;
}

export interface ReachProjectionPoint {
  day: number;
  spent: number;
  reachMin: number;
  reachAvg: number;
  reachMax: number;
}

export interface ReachProjectionSeries {
  budget: number;
  multiplier: string;
  label: string;
  data: ReachProjectionPoint[];
}

export interface ReachProjectionMetrics {
  budget: number;
  multiplier: string;
  monthlySpend: number;
  estimatedReach: number;
  estimatedLeads: number;
  estimatedCPL: number;
}

export interface KeywordResearchResult {
  topKeywords: KeywordSuggestion[];
  catalogPains: string[];
  expandedPains: string[];
  risingTerms: string[];
  valueProposition?: string;
  notes?: string;
  interests?: string;
  trendsSummary: string;
  researchedAt: string;
  reachProjection: {
    baseBudget: number;
    budgets: number[];
    cpmEstimate: { min: number; avg: number; max: number };
    series: ReachProjectionSeries[];
    metrics: ReachProjectionMetrics[];
  };
}

export interface ProductCatalogItem {
  id: string;
  name: string;
  description?: string;
  price?: number;
  status: string;
  target_segment?: string;
  core_problem?: string;
  landing_url?: string;
  differentiators?: string[] | string;
}

export interface MCAGeneratePayload {
  name: string;
  objective?: string;
  product_id?: string;
  product_or_offer: string;
  target_audience_description?: string;
  budget_initial?: number;
  landing_url?: string;
  pain_points?: string;
  approved_keywords?: string[];
  approved_pains?: string[];
  keyword_research_data?: KeywordResearchResult;
  market_niche?: string;
  meta_ad_account_id?: string;
  tone?: string;
  product_intelligence?: any;
  differentiators?: any;
  objections?: any;
  comparison?: string;
  elevator_pitch?: string;
  pitch?: string;
  proofs?: any;
  include_image?: boolean;
  include_text?: boolean;
  include_avatar?: boolean;
  avatar_gender_preference?: 'male' | 'female' | 'auto';
  selected_avatar_id?: string;
  custom_overlay_text?: string;
  visual_overlay_mode?: 'text' | 'image_in_image' | 'pure_image';
  logo_overlay_url?: string;
  regenerate_images?: boolean;
}

export interface MCAGenerateResult {
  success: boolean;
  campaignId: string;
  campaign: Partial<Campaign>;
  cgo_rationale: string;
  primary_uvp: string;
  audiences: CampaignAudience[];
  creatives: CampaignCreative[];
}

export const campaignsApi = {
  list: (init?: RequestInit) => apiFetch<{ items: Campaign[], total: number }>('/v1/crm/campaigns', init),

  create: (data: Partial<Campaign> & Record<string, any>, init?: RequestInit) => apiFetch<Campaign>('/v1/crm/campaigns', {
    ...init,
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Catálogo de Produtos e Inteligência Integrada
  getProducts: (init?: RequestInit) =>
    apiFetch<{ items?: ProductCatalogItem[] } | ProductCatalogItem[]>('/v1/crm/products', init),

  getProductIntelligence: (productId: string, init?: RequestInit) =>
    apiFetch<any>(`/v1/crm/products/${productId}/intelligence`, init),

  // Pesquisa de Palavras-Chave Google + Projeção de Alcance
  researchKeywords: (data: { product_id?: string; budget_initial?: number; custom_product_name?: string; custom_niche?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; data: KeywordResearchResult }>('/v1/crm/keywords/research', {
      ...init,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // MCA Engine Strategy Generation
  generateMCA: (data: MCAGeneratePayload, init?: RequestInit) =>
    apiFetch<MCAGenerateResult>('/v1/crm/campaigns/mca/generate', {
      ...init,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // MCA Engine Upgrade for Legacy Campaigns
  upgradeMCA: (campaignId: string, data: Partial<MCAGeneratePayload>, init?: RequestInit) =>
    apiFetch<MCAGenerateResult>(`/v1/crm/campaigns/${campaignId}/mca/upgrade`, {
      ...init,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  generateGoogleProposals: (campaignId: string, init?: RequestInit) =>
    apiFetch<GoogleProposalResult>(
      `/v1/crm/campaigns/${campaignId}/google/proposals`, { ...init, method: 'POST' }),

  updateGoogleProposals: (campaignId: string, proposals: GoogleProposals, init?: RequestInit) =>
    apiFetch<GoogleProposalResult>(`/v1/crm/campaigns/${campaignId}/google/proposals`,
      { ...init, method: 'PATCH', body: JSON.stringify(proposals) }),

  // Restore/Select Image from Version History
  restoreCreativeImage: (campaignId: string, creativeId: string, assetUrl: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; creativeId: string; assetUrl: string }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/image/restore`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ assetUrl }),
    }),

  // Delete Image from R2 and Database
  deleteCreativeImage: (campaignId: string, creativeId: string, assetUrl?: string, init?: RequestInit) =>
    apiFetch<{ success: boolean; deletedAssetUrl: string; newActiveAssetUrl: string | null }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/image${assetUrl ? `?assetUrl=${encodeURIComponent(assetUrl)}` : ''}`, {
        ...init,
        method: 'DELETE',
      }
    ),

  updateCreative: (campaignId: string, creativeId: string, data: Partial<CampaignCreative>, init?: RequestInit) =>
    apiFetch<{ success: boolean; id: string }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}`, {
      ...init,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  generateCreativeImage: (campaignId: string, creativeId: string, optionsOrInit?: { format?: 'feed' | 'stories'; isStories?: boolean } | RequestInit, init?: RequestInit) => {
    let options: { format?: 'feed' | 'stories'; isStories?: boolean } | undefined;
    let effectiveInit = init;
    if (optionsOrInit && ('format' in optionsOrInit || 'isStories' in optionsOrInit)) {
      options = optionsOrInit as { format?: 'feed' | 'stories'; isStories?: boolean };
    } else if (optionsOrInit && !init) {
      effectiveInit = optionsOrInit as RequestInit;
    }
    return apiFetch<{ success: boolean; creativeId: string; assetUrl: string; logoPlacement?: LogoPlacement | null; logoOverlayUrl?: string | null }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/image/generate`,
      {
        ...effectiveInit,
        method: 'POST',
        body: options ? JSON.stringify(options) : undefined,
      }
    );
  },

  synthesizeCreativeImage: (campaignId: string, creativeId: string, optionsOrInit?: { format?: 'feed' | 'stories'; isStories?: boolean } | RequestInit, init?: RequestInit) => {
    let options: { format?: 'feed' | 'stories'; isStories?: boolean } | undefined;
    let effectiveInit = init;
    if (optionsOrInit && ('format' in optionsOrInit || 'isStories' in optionsOrInit)) {
      options = optionsOrInit as { format?: 'feed' | 'stories'; isStories?: boolean };
    } else if (optionsOrInit && !init) {
      effectiveInit = optionsOrInit as RequestInit;
    }
    return apiFetch<{ success: boolean; creativeId: string; assetUrl: string; logoPlacement?: LogoPlacement | null; logoOverlayUrl?: string | null }>(
      `/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/image/generate`,
      {
        ...effectiveInit,
        method: 'POST',
        body: options ? JSON.stringify(options) : undefined,
      }
    );
  },

  notifyAdminWhatsApp: (campaignId: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean; adminPhone: string; result: any }>(`/v1/crm/campaigns/${campaignId}/notify-admin-whatsapp`, {
      ...init,
      method: 'POST',
    }),

  uploadCreativeImage: (campaignId: string, creativeId: string, base64: string, init?: RequestInit) =>
    apiFetch<{ id: string; assetUrl: string }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/image/upload`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ assetUrl: base64 }),
    }),

  checkOriginStatus: (id: string, init?: RequestInit) =>
    apiFetch<{ success: boolean; campaign: Campaign }>(`/v1/crm/campaigns/${id}/origin-status`, {
      ...init, method: 'POST',
    }),

  transition: (id: string, status: string, init?: RequestInit, options?: { publish?: boolean }) =>
    apiFetch<TransitionResult>(`/v1/crm/campaigns/${id}/transition`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ status, ...options }),
    }),

  getEvents: (id: string, init?: RequestInit) =>
    apiFetch<{ items: CampaignEvent[] }>(`/v1/crm/campaigns/${id}/events`, init),

  getNextStatuses: (id: string, init?: RequestInit) =>
    apiFetch<{ currentStatus: string; nextStatuses: string[] }>(`/v1/crm/campaigns/${id}/next-statuses`, init),

  processNextJob: (init?: RequestInit) =>
    apiFetch<{ processed: boolean; jobId?: string; command?: string }>('/v1/crm/campaigns/jobs/process-next', {
      ...init,
      method: 'POST',
    }),

  getBriefing: (id: string, init?: RequestInit) =>
    apiFetch<CampaignBriefing>(`/v1/crm/campaigns/${id}/briefing`, init),

  updateCampaign: (id: string, data: { name?: string; product_or_offer?: string; status?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; campaign: Campaign }>(`/v1/crm/campaigns/${id}`, {
      ...init,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  updateBriefing: (id: string, data: Partial<CampaignBriefing> & { name?: string; campaignName?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; status: string; approvedAt: string | null }>(`/v1/crm/campaigns/${id}/briefing`, {
      ...init,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAudiences: (id: string, init?: RequestInit) =>
    apiFetch<{ items: CampaignAudience[] }>(`/v1/crm/campaigns/${id}/audiences`, init),

  getCreatives: (id: string, init?: RequestInit) =>
    apiFetch<{ items: CampaignCreative[] }>(`/v1/crm/campaigns/${id}/creatives`, init),

  requestCreative: (campaignId: string, init?: RequestInit) =>
    apiFetch<{ ok: boolean, status: string }>(`/v1/crm/campaigns/${campaignId}/creative/request`, {
      ...init,
      method: 'POST',
    }),

  getMetaAccounts: (init?: RequestInit) =>
    apiFetch<{ accounts: MetaAdsAccount[] }>('/v1/crm/campaigns/meta-accounts', init),

  scheduleInstagramSequence: (id: string, options?: { addCommentTrigger?: boolean }, init?: RequestInit) =>
    apiFetch<{ success: boolean; totalScheduled: number; schedule: Array<{ postId: string; stepOrder: number; angleName: string; slotTime: string; scheduledAt: string }> }>(`/v1/crm/campaigns/${id}/instagram/schedule`, {
      ...init,
      method: 'POST',
      body: JSON.stringify(options || {})
    }),

  clearInstagramSchedule: (id: string, init?: RequestInit) =>
    apiFetch<{ success: boolean; message: string; deletedCount: number }>(`/v1/crm/campaigns/${id}/instagram/schedule`, {
      ...init,
      method: 'DELETE',
    }),

  publishInstagramCarousel: (id: string, options?: { addCommentTrigger?: boolean; caption?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; instagramMediaId: string }>(`/v1/crm/campaigns/${id}/instagram/publish-carousel`, {
      ...init,
      method: 'POST',
      body: JSON.stringify(options || {})
    }),

  publishInstagramStories: (id: string, payload?: { creativeId?: string; stepOrder?: number; imageUrl?: string; caption?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; instagramStoryId: string; creativeId?: string; stepOrder?: number; imageUrl?: string }>(`/v1/crm/campaigns/${id}/instagram/publish-stories`, {
      ...init,
      method: 'POST',
      body: JSON.stringify(payload || {})
    }),

  getInstagramPosts: (id: string, init?: RequestInit) =>
    apiFetch<{ posts: Array<{ id: string; channel: string; content: string; status: string; scheduledAt: string; publishedAt?: string; stepOrder: number; slotTime: string; angleName: string; assetUrl?: string }> }>(`/v1/crm/campaigns/${id}/instagram/posts`, init),

  analyzeCampaignIntelligence: (id: string, init?: RequestInit) =>
    apiFetch<{ success: boolean; totalAnalyzed: number; meta?: { error?: string }; google?: { error?: string } }>(`/v1/crm/campaigns/${id}/intelligence/analyze`, {
      ...init,
      method: 'POST'
    }),

  syncMetaAds: (id: string, options?: { recreateAds?: boolean }, init?: RequestInit) =>
    apiFetch<{ success: boolean; total: number; syncedCount: number; details: any[] }>(`/v1/crm/campaigns/${id}/meta-ads/sync`, {
      ...init,
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined,
      headers: {
        ...(options ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      }
    }),

  getGoogleAdsAccounts: (init?: RequestInit) =>
    apiFetch<{ accounts: Array<{ id: string; customerId: string; name: string; mode: string; isDefault: boolean }> }>('/v1/crm/campaigns/google-accounts', init),

  syncGoogleAds: (id: string, options?: { autoActivate?: boolean; recreate?: boolean; customerId?: string; landingUrl?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; customerId: string; campaignId: string; adGroupId: string; adId: string; trackingUrl: string }>(`/v1/crm/campaigns/${id}/google-ads/sync`, {
      ...init,
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined,
      headers: {
        ...(options ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      }
    }),

  getCampaignIntelligence: (id: string, init?: RequestInit) =>
    apiFetch<{ google?: GoogleIntelligenceRecord | null; items: Array<{ id: string; consciousness_level: string; angle_name: string; step_order: number; headline: string; ctr_percent: number; cpl_micros: number; audience_resonance_score: number; performance_rank: 'champion' | 'contender' | 'underperformer'; rank_reason: string; total_impressions: number; total_reach?: number; total_clicks: number; total_leads: number; total_spend_micros?: number; instagram_likes: number; instagram_comments: number; instagram_quero_count: number }> }>(`/v1/crm/campaigns/${id}/intelligence`, init),

  getCreativeMemory: (init?: RequestInit) =>
    apiFetch<{ memory: { google?: { totalCampaigns: number; totalAds: number; totalKeywords: number; prompt: string }; top_angles: string; top_consciousness_levels: string; top_cta_types: string; avg_best_ctr: number; creative_insights_prompt: string; total_campaigns_analyzed: number; total_creatives_analyzed: number } }>('/v1/crm/campaigns/intelligence/memory', init),

  getTenantAvatars: (init?: RequestInit) =>
    apiFetch<{ success: boolean; items: TenantAvatar[] }>('/v1/crm/campaigns/avatars', init),

  generateTenantAvatars: (options?: { gender?: 'male' | 'female'; product_or_offer?: string; customPrompt?: string; customName?: string; customRole?: string }, init?: RequestInit) =>
    apiFetch<{ success: boolean; items: TenantAvatar[] }>('/v1/crm/campaigns/avatars/generate', {
      ...init,
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined
    }),

  updateTenantAvatar: (avatarId: string, data: Partial<TenantAvatar>, init?: RequestInit) =>
    apiFetch<{ success: boolean; avatar: TenantAvatar }>(`/v1/crm/campaigns/avatars/${avatarId}`, {
      ...init,
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  uploadTenantAvatar: (avatarId: string, fileOrBase64: File | string, init?: RequestInit) => {
    if (typeof fileOrBase64 === 'string') {
      return apiFetch<{ success: boolean; avatar: TenantAvatar }>(`/v1/crm/campaigns/avatars/${avatarId}/upload`, {
        ...init,
        method: 'POST',
        body: JSON.stringify({ assetUrl: fileOrBase64 })
      });
    }
    const formData = new FormData();
    formData.append('file', fileOrBase64);
    return apiFetch<{ success: boolean; avatar: TenantAvatar }>(`/v1/crm/campaigns/avatars/${avatarId}/upload`, {
      ...init,
      method: 'POST',
      body: formData
    });
  },

  setVideoApiKey: (apiKey: string, provider: string = 'fal', init?: RequestInit) =>
    apiFetch<{ success: boolean; message: string }>(`/v1/crm/campaigns/video/key`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ apiKey, provider })
    }),

  getVideoKeyStatus: (init?: RequestInit) =>
    apiFetch<VideoKeyStatus>(`/v1/crm/campaigns/video/key`, {
      ...init,
      method: 'GET'
    }),

  previewCreativeVideoPrompt: (campaignId: string, creativeId: string, options?: { format?: 'vertical' | 'square'; duration?: number }, init?: RequestInit) =>
    apiFetch<{ success: boolean; creativeId: string; prompt: string; duration: number; format: string }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video/generate`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ ...options, preview: true })
    }),

  generateCreativeVideo: (campaignId: string, creativeId: string, options?: { format?: 'vertical' | 'square'; apiKey?: string; provider?: VideoProvider; prompt?: string; duration?: number }, init?: RequestInit) =>
    apiFetch<{ success: boolean; creativeId: string; videoUrl: string; videoPrompt: string; videoModel: string; duration: number; format: string }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video/generate`, {
      ...init,
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined
    }),

  uploadCreativeVideo: async (campaignId: string, creativeId: string, videoBlobOrBase64: Blob | string, init?: RequestInit) => {
    let videoDataUrl = typeof videoBlobOrBase64 === 'string' ? videoBlobOrBase64 : '';
    if (!videoDataUrl && videoBlobOrBase64 instanceof Blob) {
      videoDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Falha ao ler arquivo de vídeo'));
        reader.readAsDataURL(videoBlobOrBase64);
      });
    }
    return apiFetch<{ success: boolean; creativeId: string; videoUrl: string; videoModel: string; duration: number }>(`/v1/crm/campaigns/${campaignId}/creatives/${creativeId}/video/upload`, {
      ...init,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      },
      body: JSON.stringify({ videoDataUrl })
    });
  },

  fixTypes: (init?: RequestInit) =>
    apiFetch<{ ok: boolean, message: string }>(`/v1/crm/campaigns/fix-type`, {
      ...init,
      method: 'POST',
    }),

  listRemoteMetaCampaigns: (adAccountId?: string, init?: RequestInit) => {
    const query = adAccountId ? `?ad_account_id=${encodeURIComponent(adAccountId)}` : '';
    return apiFetch<RemoteMetaCampaignsResponse>(`/v1/crm/campaigns/meta-ads/remote${query}`, {
      ...init,
      method: 'GET',
    });
  },

  importMetaCampaigns: (campaignIds: string[], adAccountId?: string, init?: RequestInit) =>
    apiFetch<ImportMetaCampaignsResponse>(`/v1/crm/campaigns/meta-ads/import`, {
      ...init,
      method: 'POST',
      body: JSON.stringify({ campaignIds, ad_account_id: adAccountId }),
    }),
};
